'use client'

import { useState } from 'react'

import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Bot, ChartCandlestick, House, LogOut, Wallet } from 'lucide-react'

import { cn } from '@/lib/utils'

import ImageWithFallback from '../ImageFallback'

const Header = () => {
  const pathname = usePathname()
  const [open, setOpen] = useState<boolean>(false)
  const nav = [
    {
      icon: House,
      label: 'Home',
      href: '/',
    },
    {
      icon: Wallet,
      label: 'Purchase History',
      href: '/wallet',
    },
    {
      icon: ChartCandlestick,
      label: 'Trading Accounts',
      href: '/trading-accounts',
    },
    {
      icon: Bot,
      label: 'Bot Store',
      href: '/bot-store',
    },
    {
      icon: LogOut,
      label: 'Logout',
      href: '/logout',
    },
  ]

  const renderIcon = (IconComponent: React.ElementType, isActive: boolean) => {
    return <IconComponent className="lg:mt-1" size={18} color={isActive ? '#7B4BFF' : '#F5F5F6'} />
  }

  return (
    <div className="relative mb-4">
      <div className="absolute-center h-fit w-fit">
        <Image
          src="https://firebasestorage.googleapis.com/v0/b/friendtech-5438a.appspot.com/o/assets%2Faiape.svg?alt=media&token=ab41a7c1-63fc-4149-babe-bcd5cf572699"
          width={100}
          height={40}
          alt="Logo"
        />
      </div>
      {open && <div onClick={() => setOpen(false)} className="absolute inset-0 z-[100] h-screen w-screen opacity-0" />}
      <button
        onClick={() => setOpen(!open)}
        aria-label="sidebar"
        className={`${open ? 'opened' : ''} relative isolate z-[999] flex bg-transparent`}
        type="button"
      >
        <svg width="32" height="32" viewBox="0 0 100 100">
          <path
            className="line line1"
            d="M 20,29.000046 H 80.000231 C 80.000231,29.000046 94.498839,28.817352 94.532987,66.711331 94.543142,77.980673 90.966081,81.670246 85.259173,81.668997 79.552261,81.667751 75.000211,74.999942 75.000211,74.999942 L 25.000021,25.000058"
          />
          <path className="line line2" d="M 20,50 H 80" />
          <path
            className="line line3"
            d="M 20,70.999954 H 80.000231 C 80.000231,70.999954 94.498839,71.182648 94.532987,33.288669 94.543142,22.019327 90.966081,18.329754 85.259173,18.331003 79.552261,18.332249 75.000211,25.000058 75.000211,25.000058 L 25.000021,74.999942"
          />
        </svg>
      </button>

      <div
        data-toggle={open}
        className="bg-tb-grayscale-950 absolute top-0 left-0 z-[998] -mt-8 -ml-4 h-svh w-0 backdrop-blur-sm transition-[width] duration-400 ease-in-out data-[toggle=false]:pointer-events-none data-[toggle=false]:delay-[400ms] data-[toggle=true]:w-3/4 data-[toggle=true]:lg:w-1/2"
      >
        <div
          data-toggle={open}
          className="flex h-full flex-col justify-between p-4 opacity-0 transition-opacity duration-300 ease-in-out data-[toggle=false]:delay-[0ms] data-[toggle=true]:opacity-100 data-[toggle=true]:delay-[200ms]"
        >
          <div className="space-y-4">
            <div className="mt-16 border-b border-white/10">
              <Link href="/profile" passHref>
                <div onClick={() => setOpen(false)} className="flex items-center gap-x-4">
                  <div>
                    <ImageWithFallback src="/vercel.svg" fallbackSrc="" width={24} height={24} alt="" />
                  </div>
                  <p>Username</p>
                </div>
              </Link>
              <div className="p-4 pb-0"></div>
            </div>
            <ul className="space-y-6">
              {nav.map((item) => {
                const isActive = pathname === item.href
                return (
                  <li key={item.label} className="w-fit">
                    <Link href={item.href} passHref>
                      <button onClick={() => setOpen(false)} className="flex items-center gap-x-2 leading-0">
                        {renderIcon(item.icon, isActive)}
                        <span className={cn('pt-px', isActive ? 'text-tb-primary' : 'text-tb-grayscale-100')}>
                          {item.label}
                        </span>
                      </button>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          <div className="pb-4 text-center text-sm">
            <div className="bg-tb-grayscale-600 mb-2 h-px w-full" />
            <p>Copyright © 2025. All rights reserved.</p>
            <p>Version 1.0</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Header
